import { createGlobalState } from "@vueuse/core"
import { ref } from "vue"

interface EntityOptions {
  prefixPath?: string
  transformFetch?: (data: any) => any
  transformPost?: (data: any) => any
  transformPut?: (data: any) => any
  businessMode?: () => string | undefined
}

export const useEntity = ((name: string, path: string, options: EntityOptions = {}) => {

  const { public: { apiBase }, } = useRuntimeConfig();

  const $api = useApi().createInstance()
  let _path = apiBase + path;
   
  const data = ref<any[]>([])

  const fetch = async (params?: any) => {
    const response = await $api(_path, { query: params })
    data.value = response as any
    return response
  }

  const fetchBetween = async (startDate: string, endDate: string) => {
    const query: Record<string, string> = {}
    const bm = options.businessMode?.()
    if (bm) {
      query.businessMode = bm
    }
    const response = await $api(`${_path}/${startDate}/${endDate}`, { query })
    data.value = response as any
    return response
  }

  const fetchByIdBetween = async (id: string, startDate: string, endDate: string) => {
    const response = await $api(`${_path}/${encodeURIComponent(id)}/${startDate}/${endDate}`)
    data.value = response as any
    return response
  }

   const fetchByKey = async (key: string) => {
    const response = await $api(`${_path}/${encodeURIComponent(key)}`)
    data.value = response as any
    return response
  }

  // Fetch only specific rows by id (GET /<path>?ids=1,2,3). Used by the menu's
  // selective per-record refetch. Intentionally does NOT overwrite `data.value`
  // (that holds the full collection); the caller merges the returned subset.
  const fetchByIds = async (ids: Array<number | string>) => {
    const response = await $api(_path, { query: { ids: ids.join(',') } })
    return response
  }


  // Merge bound businessMode into POST body when entity is configured with the resolver
  // and the caller hasn't supplied businessMode explicitly.
  const withBusinessMode = (payload: any) => {
    const bm = options.businessMode?.()
    if (!bm) return payload
    if (payload && typeof payload === 'object' && !Array.isArray(payload) && !('businessMode' in payload)) {
      return { ...payload, businessMode: bm }
    }
    return payload
  }

  //insert
  const post = async (payload: any) => {
    const finalPayload = withBusinessMode(payload)
    const response = await $api(_path, { method: 'POST', body: JSON.stringify(finalPayload) });
    return response
  }

  const postWithKey =async (partitionKey : string, payload :any) => {
      const response = await $api(_path + '/' + encodeURIComponent(partitionKey), { method: 'POST', body: JSON.stringify(payload) });
    return response
  }

  // POST /<path>/<id>/change-password — id is in the URL, body carries the
  // credentials. Mirrors postWithKey's fetch/URL/body mechanics.
  const changePassword = async (id: string, payload: any) => {
    const response = await $api(`${_path}/${encodeURIComponent(id)}/change-password`, { method: 'POST', body: JSON.stringify(payload) });
    return response
  }

  const postWithQuery = async (query: Record<string, any>, payload?: any) => {
    const response = await $api(_path, {
      method: 'POST',
      query,
      body: payload ? JSON.stringify(payload) : undefined,
    });
    return response
  }


  //update
  const put = async (id: string, payload: any, queryParams?: Record<string, string | number>) => {
    const response = await $api(_path + '/' + encodeURIComponent(id), { method: 'PUT', body: JSON.stringify(payload) });
    return response
  }


  //delete
  const remove = async (id: string, payload?: any) => {
    const response = await $api(_path + '/' + encodeURIComponent(id), {
      method: 'DELETE',
      ...(payload && { body: JSON.stringify(payload) })
    });
    return response
  }

  // DynamoDB format methods - use partitionKey/sortKey pattern
  // Note: partitionKey is already part of the path from useDataservice
  const postByDynamoFormat = async (_partitionKey: string, payload: any) => {
    const response = await $api(_path, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    return response
  }

  const putByDynamoFormat = async (_partitionKey: string, sortKey: string, payload: any) => {
    const response = await $api(_path + '/' + encodeURIComponent(sortKey), {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    return response
  }

  const removeByDynamoFormat = async (_partitionKey: string, sortKey: string, payload?: any) => {
    const response = await $api(_path + '/' + encodeURIComponent(sortKey), {
      method: 'DELETE',
      ...(payload && { body: JSON.stringify(payload) })
    });
    return response
  }

  return {
    fetch,
    fetchBetween,
    fetchByIdBetween,
    fetchByKey,
    fetchByIds,
    data,
    post,
    postWithKey,
    changePassword,
    postWithQuery,
    put,
    remove,
    postByDynamoFormat,
    putByDynamoFormat,
    removeByDynamoFormat,
  }
})
